import { Box, Flex, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import {
  Download,
  File,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import { Button } from "components/ds";
import {
  evidenceKind,
  formatFileSize,
  type EvidenceKind,
} from "../reviewLogic";
import type { EvidenceFile } from "state/members/accessReviewsEvidence";

/**
 * EvidenceCard — an uploaded-evidence row (PLO-4818, design D). Shows a
 * type-tinted 32px icon tile, the file name, a "TYPE · size" meta line, and
 * quiet download + delete actions. Type tints follow the familiar file-type
 * colours (CSV green, images azurite, PDF red).
 */

const KIND_STYLE: Record<
  EvidenceKind,
  { icon: typeof File; bg: string; color: string; label: string }
> = {
  image: {
    icon: ImageIcon,
    bg: "p.50",
    color: "text.body.azurite",
    label: "Image",
  },
  pdf: {
    icon: FileText,
    bg: "error.50",
    color: "text.body.error",
    label: "PDF",
  },
  csv: {
    icon: FileSpreadsheet,
    bg: "success.50",
    color: "text.body.success",
    label: "CSV",
  },
  doc: {
    icon: FileText,
    bg: "bg.section",
    color: "text.body.tertiary",
    label: "Doc",
  },
  file: {
    icon: File,
    bg: "bg.section",
    color: "text.body.tertiary",
    label: "File",
  },
};

export interface EvidenceCardProps {
  file: EvidenceFile;
  onDownload?: (file: EvidenceFile) => void;
  onDelete?: (file: EvidenceFile) => void;
  isDeleting?: boolean;
  isDownloading?: boolean;
}

// The extension makes the sharpest label (PNG vs JPG vs CSV); fall back to the
// kind label when the filename carries no extension.
const typeLabel = (filename: string, kind: EvidenceKind): string => {
  const ext = filename.includes(".")
    ? filename.split(".").pop()?.toUpperCase()
    : "";
  return ext || KIND_STYLE[kind].label;
};

const EvidenceCard = ({
  file,
  onDownload,
  onDelete,
  isDeleting,
  isDownloading,
}: EvidenceCardProps) => {
  const kind = evidenceKind(file.filename, file.content_type);
  const style = KIND_STYLE[kind];

  return (
    <HStack
      spacing="sm"
      p="sm"
      borderRadius="component.2xl"
      border="1px solid"
      borderColor="border.default"
      bg="bg.container"
      transition="border-color 0.15s, background-color 0.15s"
      _hover={{ borderColor: "border.strong" }}
      justify="space-between"
    >
      <HStack spacing="sm" flex="1" minW={0}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="component.md"
          bg={style.bg}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={style.icon} boxSize="18px" color={style.color} />
        </Flex>
        <Box minW={0} flex="1">
          <Text
            fontSize="smd"
            fontWeight={500}
            color="text.body.primary"
            noOfLines={1}
          >
            {file.filename}
          </Text>
          <Text fontSize="xs" color="text.body.tertiary">
            {typeLabel(file.filename, kind)} · {formatFileSize(file.file_size)}
          </Text>
        </Box>
      </HStack>

      <HStack spacing="1xs" flexShrink={0}>
        {onDownload && (
          <Tooltip label="Download" hasArrow placement="top">
            <Button
              aria-label="Download evidence"
              icon={<Icon as={Download} boxSize={4} />}
              size="sm"
              variant="subtle"
              tone="neutral"
              isLoading={isDownloading}
              onClick={() => onDownload(file)}
            />
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip label="Delete" hasArrow placement="top">
            <Button
              aria-label="Delete evidence"
              icon={<Icon as={Trash2} boxSize={4} />}
              size="sm"
              variant="subtle"
              tone="neutral"
              isLoading={isDeleting}
              onClick={() => onDelete(file)}
            />
          </Tooltip>
        )}
      </HStack>
    </HStack>
  );
};

export default EvidenceCard;
